import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Settings, Building2, Clock, AlertCircle, Loader2, CheckCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

interface UserProfile {
  full_name: string;
  email: string;
  company_name: string | null;
}

interface BusinessProfile {
  name: string;
  description: string;
  category: string;
  timezone: string;
}

const TIMEZONES = [
  { value: 'America/Argentina/Buenos_Aires', label: '(GMT-03:00) Buenos Aires' },
  { value: 'America/Santiago', label: '(GMT-04:00) Santiago' },
  { value: 'America/Bogota', label: '(GMT-05:00) Bogotá' },
  { value: 'America/Mexico_City', label: '(GMT-06:00) Ciudad de México' },
  { value: 'America/Lima', label: '(GMT-05:00) Lima' },
];

const CATEGORIES = [
  'Comercio minorista',
  'Servicios profesionales',
  'Restaurante',
  'Educación',
  'Salud y bienestar',
  'Tecnología',
  'Otros',
];

export default function Profile() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile>({
    full_name: '',
    email: user?.email || '',
    company_name: null,
  });
  const [businessProfile, setBusinessProfile] = useState<BusinessProfile>({
    name: '',
    description: '',
    category: '',
    timezone: 'America/Argentina/Buenos_Aires',
  });
  const [businessProfileId, setBusinessProfileId] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      fetchProfiles();
    }
  }, [user]);

  async function fetchProfiles() {
    try {
      setLoading(true);
      setError(null);

      // Fetch user profile
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user!.id)
        .maybeSingle();

      if (profileError && profileError.code !== 'PGRST116') {
        throw profileError;
      }

      if (profileData) {
        setUserProfile({
          full_name: profileData.full_name || '',
          email: profileData.email || user!.email || '',
          company_name: profileData.company_name,
        });
      } else {
        // If no profile exists, create one
        const { error: insertError } = await supabase
          .from('profiles')
          .insert({
            id: user!.id,
            email: user!.email,
            full_name: user!.user_metadata?.full_name || '',
          });

        if (insertError) throw insertError;

        setUserProfile({
          full_name: user!.user_metadata?.full_name || '',
          email: user!.email || '',
          company_name: null,
        });
      }

      // First get the business_profile_id from instances
      const { data: instanceData, error: instanceError } = await supabase
        .from('instances')
        .select('business_profile_id')
        .eq('user_id', user!.id)
        .maybeSingle();

      if (instanceError) throw instanceError;

      if (instanceData?.business_profile_id) {
        setBusinessProfileId(instanceData.business_profile_id);

        // Then fetch the business profile data
        const { data: businessData, error: businessError } = await supabase
          .from('business_profiles')
          .select('*')
          .eq('id', instanceData.business_profile_id)
          .maybeSingle();

        if (businessError) throw businessError;

        if (businessData) {
          setBusinessProfile({
            name: businessData.name || '',
            description: businessData.description || '',
            category: businessData.category || '',
            timezone: businessData.timezone || 'America/Argentina/Buenos_Aires',
          });
        }
      }

    } catch (err: any) {
      console.error('Error fetching profiles:', err);
      setError('Error al cargar los perfiles');
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);

      // Update user profile
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({
          id: user!.id,
          full_name: userProfile.full_name,
          email: userProfile.email,
          company_name: userProfile.company_name,
          updated_at: new Date().toISOString(),
        });

      if (profileError) throw profileError;

      // Update or create business profile
      let finalBusinessProfileId = businessProfileId;

      if (!businessProfileId) {
        // Create new business profile
        const { data: newProfile, error: createError } = await supabase
          .from('business_profiles')
          .insert({
            user_id: user!.id,
            name: businessProfile.name,
            description: businessProfile.description,
            category: businessProfile.category,
            timezone: businessProfile.timezone,
          })
          .select()
          .single();

        if (createError) throw createError;
        finalBusinessProfileId = newProfile.id;
      } else {
        // Update existing business profile
        const { error: updateError } = await supabase
          .from('business_profiles')
          .update({
            name: businessProfile.name,
            description: businessProfile.description,
            category: businessProfile.category,
            timezone: businessProfile.timezone,
            updated_at: new Date().toISOString(),
          })
          .eq('id', businessProfileId);

        if (updateError) throw updateError;
      }

      // Update instance with business profile reference
      const { error: instanceError } = await supabase
        .from('instances')
        .update({
          business_profile_id: finalBusinessProfileId,
        })
        .eq('user_id', user!.id);

      if (instanceError) throw instanceError;

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);

    } catch (err: any) {
      console.error('Error updating profiles:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <User className="h-8 w-8 mr-3 text-gray-700" />
            Perfil de Usuario
          </h1>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Volver al Dashboard
          </button>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-6 bg-green-50 border-l-4 border-green-400 p-4">
            <div className="flex">
              <CheckCircle className="h-5 w-5 text-green-400" />
              <div className="ml-3">
                <p className="text-sm text-green-700">
                  Los cambios se guardaron correctamente
                </p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="bg-white shadow rounded-lg overflow-hidden">
            {/* User Profile Section */}
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900 flex items-center mb-4">
                <Settings className="h-5 w-5 mr-2 text-gray-500" />
                Información Personal
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Nombre completo
                  </label>
                  <input
                    type="text"
                    value={userProfile.full_name}
                    onChange={(e) => setUserProfile({
                      ...userProfile,
                      full_name: e.target.value
                    })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Correo electrónico
                  </label>
                  <input
                    type="email"
                    value={userProfile.email}
                    disabled
                    className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm text-gray-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Nombre de la empresa
                  </label>
                  <input
                    type="text"
                    value={userProfile.company_name || ''}
                    onChange={(e) => setUserProfile({
                      ...userProfile,
                      company_name: e.target.value
                    })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
              </div>
            </div>

            {/* Business Profile Section */}
            <div className="p-6">
              <h2 className="text-lg font-medium text-gray-900 flex items-center mb-4">
                <Building2 className="h-5 w-5 mr-2 text-[#25D366]" />
                Perfil de WhatsApp Business
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Nombre del negocio
                  </label>
                  <input
                    type="text"
                    value={businessProfile.name}
                    onChange={(e) => setBusinessProfile({
                      ...businessProfile,
                      name: e.target.value
                    })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#25D366] focus:ring-[#25D366]"
                    placeholder="Ej: Mi Empresa S.A."
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Este nombre será visible para tus clientes en WhatsApp
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Categoría
                  </label>
                  <select
                    value={businessProfile.category}
                    onChange={(e) => setBusinessProfile({
                      ...businessProfile,
                      category: e.target.value
                    })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#25D366] focus:ring-[#25D366]"
                  >
                    <option value="">Selecciona una categoría</option>
                    {CATEGORIES.map(category => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Descripción
                  </label>
                  <textarea
                    value={businessProfile.description}
                    onChange={(e) => setBusinessProfile({
                      ...businessProfile,
                      description: e.target.value
                    })}
                    rows={3}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#25D366] focus:ring-[#25D366]"
                    placeholder="Cuéntales a tus clientes sobre tu empresa..."
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Máximo 512 caracteres
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Zona horaria
                  </label>
                  <select
                    value={businessProfile.timezone}
                    onChange={(e) => setBusinessProfile({
                      ...businessProfile,
                      timezone: e.target.value
                    })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#25D366] focus:ring-[#25D366]"
                  >
                    {TIMEZONES.map(tz => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="bg-gray-50 px-6 py-3 flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className="flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  'Guardar cambios'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}